import type { LucideIcon } from 'lucide-react';

import { formatDate } from '@/lib/format';

/*
  The filter model the design's filter bar and drawer share (.ord-filterbar, .ord-drawer in the
  prototype, whose CSS says of the drawer: "the Users register reuses the same drawer").

  A screen declares its filters as data and gets the whole mechanism: the drawer that edits them,
  the bar that shows the applied ones as removable chips, the active count, and the predicates.
  Nothing about a particular register lives in here, so the next screen that needs filters
  supplies a config rather than a component.

  One value per filter. A text, date or choice filter holds a string, because the applied set has
  to survive a round trip through the URL; a `multi` filter holds the list of what is ticked,
  which cannot be one string without an escaping rule for whatever separates them. Neutral means
  "not applied": an empty text box, a choice field's own "Any" option, or nothing ticked. Neutral
  values are never written to the URL and never draw a chip.
*/

export interface FilterOption {
    value: string;
    label: string;
}

/** An option that also says how many rows carry it, for the checkbox lists that show counts. */
export interface CountedFilterOption extends FilterOption {
    count: number;
}

interface FilterFieldBase<K extends string> {
    key: K;
    label: string;
    /** The chip's prefix, shorter than the label where the label would crowd the bar. */
    chipLabel: string;
    /**
     * Whether the drawer greys this field out, given the set being edited, for a filter the design
     * offers only once another one answers a particular way (Commission Model under Supplier
     * Commission). Presentation only: the API takes every filter on its own, and a greyed field is
     * reset to neutral rather than left holding a value nobody can see or remove.
     */
    disabledWhen?: (applied: AppliedFilters<K>) => boolean;
}

export interface TextFilterField<K extends string> extends FilterFieldBase<K> {
    kind: 'text';
    placeholder?: string;
    /** A search across several columns, drawn with the search field's magnifier (ALS-275). */
    search?: boolean;
    /**
     * Compare digits only, dropping spaces, brackets and dashes from both sides, so a contact
     * number matches however either side punctuated it.
     */
    digitsOnly?: boolean;
}

export interface ChoiceFilterField<K extends string> extends FilterFieldBase<K> {
    kind: 'choice';
    /** The option meaning "not applied"; must be one of `options`. */
    neutral: string;
    /**
     * The choices, or a function of the set being edited when one filter narrows another (the
     * secondary categories under the chosen primary). The neutral option must be among them.
     */
    options: readonly FilterOption[] | ((applied: AppliedFilters<K>) => readonly FilterOption[]);
    /** A list too long to scroll (3,000 brands) is searched: the drawer draws a Combobox rather than a Select. */
    searchable?: boolean;
}

/** One calendar day, carried as YYYY-MM-DD; the drawer draws a date field and the chip prints the day. */
export interface DateFilterField<K extends string> extends FilterFieldBase<K> {
    kind: 'date';
}

/**
 * Two days as one control, carried as `YYYY-MM-DD..YYYY-MM-DD`.
 *
 * The design draws a From / To range as ONE field opening a two-month calendar rather than two
 * boxes (2497:22154, and the prototype's own note: "not two boxes"). Two `date` fields would work
 * and read wrong, and they would also let somebody set an end before a start with nothing to say
 * so. One value keeps the pair together through the URL, the chip and the API.
 *
 * Either end may be missing: `2026-01-01..` is "from January", `..2026-01-01` is "until January".
 */
export interface DateRangeFilterField<K extends string> extends FilterFieldBase<K> {
    kind: 'daterange';
}

/** What joins the two ends of a range in one value. Two dots, because a date cannot contain them. */
export const RANGE_SEPARATOR = '..';

/** The two ends of a range value, either of which may be empty. */
export function rangeEnds(value: string | undefined): { from?: string; to?: string } {
    const [from = '', to = ''] = (value ?? '').split(RANGE_SEPARATOR);
    return { from: from.trim() || undefined, to: to.trim() || undefined };
}

/** The two ends as one value, or an empty string when neither is set. */
export function rangeValue(from: string | undefined, to: string | undefined): string {
    return from || to ? `${from ?? ''}${RANGE_SEPARATOR}${to ?? ''}` : '';
}

/**
 * Several at once, as a list of checkboxes rather than a select, each saying how many rows it
 * would leave. The design's wardrobe drawer draws its locations this way (3160:2): a location is
 * not one of a set of alternatives the way a role is, and the reader picks the two rooms they are
 * looking in.
 */
export interface MultiFilterField<K extends string> extends FilterFieldBase<K> {
    kind: 'multi';
    options: readonly CountedFilterOption[];
    /** What one option's count reads as beside it, e.g. `(n) => n === 1 ? '1 piece' : `${n} pieces``. */
    countLabel: (count: number) => string;
}

export type FilterField<K extends string> =
    | TextFilterField<K>
    | ChoiceFilterField<K>
    | DateFilterField<K>
    | DateRangeFilterField<K>
    | MultiFilterField<K>;

/**
 * What an applied filter holds: a string for a text, date or choice field, the ticked values for
 * a multi one. The two accessors below are how a caller reads it without narrowing by hand.
 */
export type FilterValue = string | readonly string[];

/**
 * What joins a multi filter's values in a URL. A comma, because that is what a reader expects to
 * see in `?locations=Bedroom,RAIL 7`, and because the one screen with a multi filter forbids a
 * comma in the values themselves for exactly this reason (ALS-52). A field whose values could
 * contain one needs its own encoding, not a different separator here.
 */
export const MULTI_SEPARATOR = ',';

/**
 * Whether a value is a multi field's list. `Array.isArray` alone widens a readonly array to
 * `any[]`, so the guard is written out and every reader goes through it.
 */
export function isFilterList(value: FilterValue | undefined): value is readonly string[] {
    return Array.isArray(value);
}

/** A text, date or choice field's value, or undefined. A list is not one, so it reads as absent. */
export function filterText<K extends string>(
    applied: AppliedFilters<K>,
    key: K,
): string | undefined {
    const value = applied[key];
    return isFilterList(value) ? undefined : value;
}

/** A multi field's ticked values. Anything else reads as nothing ticked. */
export function filterChoices<K extends string>(
    applied: AppliedFilters<K>,
    key: K,
): readonly string[] {
    const value = applied[key];
    return isFilterList(value) ? value : [];
}

/** A choice field's options for the set being edited; static lists ignore the set. */
export function fieldOptions<K extends string>(
    field: ChoiceFilterField<K>,
    applied: AppliedFilters<K>,
): readonly FilterOption[] {
    return typeof field.options === 'function' ? field.options(applied) : field.options;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** "12 Jun 2026" for a chip; the raw text when it is not a day. */
function formatDay(value: string): string {
    return ISO_DAY.test(value) ? formatDate(`${value}T00:00:00`) : value;
}

export interface FilterGroup<K extends string> {
    key: string;
    icon: LucideIcon;
    title: string;
    subtitle: string;
    fields: readonly FilterField<K>[];
    /**
     * How wide the group's field grid may get. Three by default, which is what the registers
     * with many filters want; two where the frame pairs them, as the wardrobe's does.
     */
    columns?: 2 | 3;
}

/** The applied set. A missing key, an empty string, a choice's neutral option or an empty list all mean "not applied". */
export type AppliedFilters<K extends string> = Partial<Record<K, FilterValue>>;

/** Every field across every group, in the order the drawer draws them. */
export function filterFields<K extends string>(
    groups: readonly FilterGroup<K>[],
): FilterField<K>[] {
    return groups.flatMap((group) => [...group.fields]);
}

export function findFilterField<K extends string>(
    groups: readonly FilterGroup<K>[],
    key: K,
): FilterField<K> | undefined {
    return filterFields(groups).find((field) => field.key === key);
}

/** Whether this value leaves the field unapplied. */
export function isNeutral<K extends string>(
    field: FilterField<K>,
    value: FilterValue | undefined,
): boolean {
    if (value === undefined) return true;
    if (field.kind === 'multi') return !isFilterList(value) || value.length === 0;
    if (isFilterList(value)) return true;
    if (field.kind === 'daterange') {
        const { from, to } = rangeEnds(value);
        return from === undefined && to === undefined;
    }
    if (field.kind === 'text' || field.kind === 'date') return value.trim() === '';
    return value === field.neutral;
}

/** The applied fields, in drawer order: what the bar draws as chips and what the count counts. */
export function activeFilterFields<K extends string>(
    groups: readonly FilterGroup<K>[],
    applied: AppliedFilters<K>,
): FilterField<K>[] {
    return filterFields(groups).filter((field) => !isNeutral(field, applied[field.key]));
}

export function activeFilterCount<K extends string>(
    groups: readonly FilterGroup<K>[],
    applied: AppliedFilters<K>,
): number {
    return activeFilterFields(groups, applied).length;
}

/** How many of one group's fields are applied, for the badge on its accordion head. */
export function groupFilterCount<K extends string>(
    group: FilterGroup<K>,
    applied: AppliedFilters<K>,
): number {
    return group.fields.filter((field) => !isNeutral(field, applied[field.key])).length;
}

/** A choice shows its option's label on the chip, never the raw value the URL carries; a date prints as a day. */
export function filterValueLabel<K extends string>(
    field: FilterField<K>,
    value: string,
    applied: AppliedFilters<K> = {},
): string {
    if (field.kind === 'text') return value.trim();
    if (field.kind === 'date') return formatDay(value.trim());
    if (field.kind === 'daterange') {
        const { from, to } = rangeEnds(value);
        if (from && to) return `${formatDay(from)} to ${formatDay(to)}`;
        return from ? `from ${formatDay(from)}` : `until ${formatDay(to!)}`;
    }
    if (field.kind === 'multi') {
        return field.options.find((option) => option.value === value)?.label ?? value;
    }
    return fieldOptions(field, applied).find((option) => option.value === value)?.label ?? value;
}

/** The chip's whole text: "Role: Admin". */
export function filterChipText<K extends string>(
    field: FilterField<K>,
    value: string,
    applied: AppliedFilters<K> = {},
): string {
    return `${field.chipLabel}: ${filterValueLabel(field, value, applied)}`;
}

/**
 * The chips one field draws. A text, date or choice field draws one; a multi field draws ONE PER
 * TICKED VALUE, so three locations can be narrowed to two by taking one chip off rather than
 * reopening the drawer. That is what the prototype does with the wardrobe's locations.
 */
export function filterChips<K extends string>(
    field: FilterField<K>,
    value: FilterValue | undefined,
    applied: AppliedFilters<K> = {},
): { value: string; text: string }[] {
    if (isNeutral(field, value)) return [];
    if (isFilterList(value)) {
        return value.map((one) => ({ value: one, text: filterChipText(field, one, applied) }));
    }
    return [{ value: value as string, text: filterChipText(field, value as string, applied) }];
}

/** Every filter back to neutral. Clearing writes nothing rather than writing the neutral values. */
export function clearedFilters<K extends string>(): AppliedFilters<K> {
    return {};
}

/**
 * The applied set with the neutral values dropped, which is what goes in the URL and what two
 * sets are compared as, so `{}` and `{ role: 'Any' }` count as the same set.
 */
export function appliedFiltersOnly<K extends string>(
    groups: readonly FilterGroup<K>[],
    applied: AppliedFilters<K>,
): AppliedFilters<K> {
    const out: AppliedFilters<K> = {};
    for (const field of activeFilterFields(groups, applied)) {
        const value = applied[field.key];
        if (value === undefined) continue;
        out[field.key] = field.kind === 'choice' || isFilterList(value) ? value : value.trim();
    }
    return out;
}

/** Whether two sets would give the same list, ignoring how the neutrals are spelled. */
export function sameFilters<K extends string>(
    groups: readonly FilterGroup<K>[],
    a: AppliedFilters<K>,
    b: AppliedFilters<K>,
): boolean {
    const left = appliedFiltersOnly(groups, a);
    const right = appliedFiltersOnly(groups, b);
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    for (const key of keys) {
        const mine = left[key as K];
        const theirs = right[key as K];
        if (isFilterList(mine) || isFilterList(theirs)) {
            // Order is how they were ticked, not part of what is filtered, so two sets with the
            // same members are the same set and the drawer is not dirty for having reordered them.
            const one = [...(isFilterList(mine) ? mine : [])].sort();
            const two = [...(isFilterList(theirs) ? theirs : [])].sort();
            if (one.length !== two.length || one.some((value, i) => value !== two[i])) return false;
            continue;
        }
        if (mine !== theirs) return false;
    }
    return true;
}

/**
 * The applied set as a URL's search params: every value a string, multi filters joined. The
 * inverse of `sanitiseFilters`, which splits them back.
 */
export function urlFilters<K extends string>(
    groups: readonly FilterGroup<K>[],
    applied: AppliedFilters<K>,
): Partial<Record<K, string>> {
    const out: Partial<Record<K, string>> = {};
    const only = appliedFiltersOnly(groups, applied);
    for (const key of Object.keys(only) as K[]) {
        const value: FilterValue | undefined = only[key];
        if (value === undefined) continue;
        out[key] = isFilterList(value) ? value.join(MULTI_SEPARATOR) : value;
    }
    return out;
}

/** The text rule the design states: matches anywhere in the value, case-insensitively. */
export function matchesText(haystack: string | null | undefined, needle: string): boolean {
    const term = needle.trim().toLowerCase();
    if (!term) return true;
    return (haystack ?? '').toLowerCase().includes(term);
}

/** The contact-number rule: compare the digits, so punctuation and spacing on either side stop mattering. */
export function matchesDigits(haystack: string | null | undefined, needle: string): boolean {
    const term = needle.replace(/\D/g, '');
    if (!term) return true;
    return (haystack ?? '').replace(/\D/g, '').includes(term);
}

/**
 * The predicate for one field, from the field's own kind: text fields compare as text (or as
 * digits when they ask to), choice fields compare with the reader's own matcher. Keeps a screen's
 * filter file to one line per filter.
 */
export function matchesFilterText<K extends string>(
    field: TextFilterField<K>,
    value: string | undefined,
    haystack: string | null | undefined,
): boolean {
    if (isNeutral(field, value) || value === undefined) return true;
    return field.digitsOnly ? matchesDigits(haystack, value) : matchesText(haystack, value);
}

/** The multi rule: nothing ticked matches everything, otherwise the value has to be one ticked. */
export function matchesFilterChoices(
    chosen: readonly string[],
    value: string | null | undefined,
): boolean {
    if (chosen.length === 0) return true;
    return chosen.includes(value ?? '');
}

/**
 * The applied set as read from an untrusted source, usually the URL: text is trimmed, a choice
 * whose value is not one of its options is dropped rather than allowed to empty the list, and
 * neutral values are dropped so they never draw a chip. The route schema therefore only has to
 * say "optional strings", and what counts as a usable value stays here with the filters.
 */
export function sanitiseFilters<K extends string>(
    groups: readonly FilterGroup<K>[],
    raw: Partial<Record<K, FilterValue | undefined>>,
): AppliedFilters<K> {
    const out: AppliedFilters<K> = {};
    for (const field of filterFields(groups)) {
        const value = raw[field.key];
        if (value === undefined) continue;
        if (field.kind === 'multi') {
            // A string is what a URL carries: the values joined. Splitting it here is what makes
            // `?locations=Bedroom,RAIL 7` the same set the drawer ticked. A value the field no
            // longer offers is dropped rather than kept: a location deleted while the drawer was
            // open would otherwise filter the grid down to nothing.
            const offered = isFilterList(value) ? value : value.split(MULTI_SEPARATOR);
            const kept = offered.filter((one) =>
                field.options.some((option) => option.value === one),
            );
            if (kept.length > 0) out[field.key] = kept;
            continue;
        }
        if (isFilterList(value)) continue;
        if (field.kind === 'text') {
            const trimmed = value.trim();
            if (trimmed !== '') out[field.key] = trimmed;
            continue;
        }
        if (field.kind === 'date') {
            const trimmed = value.trim();
            if (ISO_DAY.test(trimmed)) out[field.key] = trimmed;
            continue;
        }
        if (field.kind === 'daterange') {
            // Either end may be missing, and an end that is not a day is dropped rather than
            // taken, so a hand-edited URL cannot put nonsense in the calendar.
            const { from, to } = rangeEnds(value);
            const start = from && ISO_DAY.test(from) ? from : undefined;
            const end = to && ISO_DAY.test(to) ? to : undefined;
            const cleaned = rangeValue(start, end);
            if (cleaned) out[field.key] = cleaned;
            continue;
        }
        if (value === field.neutral) continue;
        // Against the options the rest of the raw set would give: a secondary category is only
        // usable under the primary the URL also names.
        if (fieldOptions(field, out).some((option) => option.value === value))
            out[field.key] = value;
    }
    return out;
}
